import { IncidentWorkflowsQuery } from '@/graphql/generated/operations'
import { WorkflowState } from '@/graphql/generated/schemas'
import { formatFullName } from '@/utils/formats'

import { WorkflowsRowDataType } from '../types/types'

export type WorkflowsSortColumn = 'createdAt' | 'incidentType'

export const SORTABLE_COLUMNS: WorkflowsSortColumn[] = [
  'createdAt',
  'incidentType',
]

/**
 * Maps raw `incidentWorkflows` API data into table row objects, then applies
 * client-side filtering (by incidentType name) and sorting (by createdAt or
 * incidentType). Used by `useWorkflowsTable` inside a `useMemo`.
 */
export const formatWorkflowRows = (
  data: IncidentWorkflowsQuery | undefined,
  filter: string,
  sortColumn: WorkflowsSortColumn | null,
  sortOrder: 'asc' | 'desc' | null
): WorkflowsRowDataType[] => {
  const workflows = data?.incidentWorkflows ?? []

  const rows: WorkflowsRowDataType[] = workflows.map((w) => ({
    id: w.versionId,
    workflowId: w.workflowId,
    createdAt: w.createTime,
    incidentType: w.name,
    creator: (w.creator && formatFullName(w.creator).trim()) || w.creatorId,
    isArchived: w.state === WorkflowState.Archived,
  }))

  const filtered = filter
    ? rows.filter((r) =>
        r.incidentType.toLowerCase().includes(filter.toLowerCase())
      )
    : rows

  if (!sortColumn || !sortOrder) return filtered

  return [...filtered].sort((a, b) => {
    if (sortColumn === 'createdAt') {
      const diff =
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      return sortOrder === 'asc' ? diff : -diff
    }
    if (sortColumn === 'incidentType') {
      return sortOrder === 'asc'
        ? a.incidentType.localeCompare(b.incidentType)
        : b.incidentType.localeCompare(a.incidentType)
    }
    return 0
  })
}
